import { supabase } from '../../integrations/supabase/client';
import type {
  IComplianceRepository,
  ComplianceFramework,
  ComplianceRequirement,
  EntityComplianceStatus,
  ComplianceAssessment,
  ComplianceAction,
  ComplianceSummary,
  ApplicableFramework,
  TenantComplianceConfig,
} from '../interfaces/IComplianceRepository';

export class SupabaseComplianceRepository implements IComplianceRepository {
  // Frameworks
  async getFrameworks(): Promise<ComplianceFramework[]> {
    const { data, error } = await (supabase as any)
      .from('compliance_frameworks')
      .select('*')
      .eq('is_active', true)
      .order('framework_name');

    if (error) throw error;
    return data || [];
  }

  async getFrameworkById(frameworkId: string): Promise<ComplianceFramework | null> {
    const { data, error } = await (supabase as any)
      .from('compliance_frameworks')
      .select('*')
      .eq('id', frameworkId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data;
  }

  async getFrameworkByCode(code: string): Promise<ComplianceFramework | null> {
    const { data, error } = await (supabase as any)
      .from('compliance_frameworks')
      .select('*')
      .eq('framework_code', code)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data;
  }

  // Requirements
  async getRequirementsByFramework(frameworkId: string): Promise<ComplianceRequirement[]> {
    const { data, error } = await (supabase as any)
      .from('compliance_requirements')
      .select('*')
      .eq('framework_id', frameworkId)
      .order('requirement_code');

    if (error) throw error;
    return data || [];
  }

  async getRequirementById(requirementId: string): Promise<ComplianceRequirement | null> {
    const { data, error } = await (supabase as any)
      .from('compliance_requirements')
      .select('*')
      .eq('id', requirementId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data;
  }

  // Entity Compliance Status
  async getEntityComplianceStatus(entityType: string, entityId: string): Promise<EntityComplianceStatus[]> {
    const { data, error} = await (supabase as any)
      .from('entity_compliance_status')
      .select(`
        *,
        framework:compliance_frameworks(*),
        requirement:compliance_requirements(*)
      `)
      .eq('entity_type', entityType)
      .eq('entity_id', entityId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  async getEntityComplianceByFramework(
    entityType: string,
    entityId: string,
    frameworkId: string
  ): Promise<EntityComplianceStatus[]> {
    const { data, error } = await (supabase as any)
      .from('entity_compliance_status')
      .select(`
        *,
        framework:compliance_frameworks(*),
        requirement:compliance_requirements(*)
      `)
      .eq('entity_type', entityType)
      .eq('entity_id', entityId)
      .eq('framework_id', frameworkId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  async updateComplianceStatus(
    statusId: string,
    updates: Partial<EntityComplianceStatus>
  ): Promise<EntityComplianceStatus> {
    const { data, error } = await (supabase as any)
      .from('entity_compliance_status')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', statusId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  // Compliance Summary
  async getComplianceSummary(entityType: string, entityId: string): Promise<ComplianceSummary> {
    const { data, error } = await (supabase as any)
      .rpc('get_compliance_summary', {
        p_entity_type: entityType,
        p_entity_id: entityId,
      });

    if (error) throw error;
    return data || {
      overall_score: 0,
      frameworks: [],
      total_requirements: 0,
      remediation_actions: 0,
      overdue_actions: 0,
    };
  }

  async calculateComplianceScore(
    entityType: string,
    entityId: string,
    frameworkId?: string
  ): Promise<number> {
    const { data, error } = await (supabase as any)
      .rpc('calculate_compliance_score', {
        p_entity_type: entityType,
        p_entity_id: entityId,
        p_framework_id: frameworkId || null,
      });

    if (error) throw error;
    return data || 0;
  }

  // Assessments
  async getAssessments(entityType: string, entityId: string): Promise<ComplianceAssessment[]> {
    const { data, error } = await (supabase as any)
      .from('compliance_assessments')
      .select(`
        *,
        framework:compliance_frameworks(*)
      `)
      .eq('entity_type', entityType)
      .eq('entity_id', entityId)
      .order('assessment_date', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  async createAssessment(assessment: Partial<ComplianceAssessment>): Promise<ComplianceAssessment> {
    const { data, error } = await (supabase as any)
      .from('compliance_assessments')
      .insert(assessment)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  // Actions
  async getActions(entityType: string, entityId: string): Promise<ComplianceAction[]> {
    const { data, error } = await (supabase as any)
      .from('compliance_actions')
      .select(`
        *,
        compliance_status:entity_compliance_status(*)
      `)
      .eq('compliance_status.entity_type', entityType)
      .eq('compliance_status.entity_id', entityId)
      .order('due_date', { ascending: true });

    if (error) throw error;
    return data || [];
  }

  async getActionsByStatus(status: string): Promise<ComplianceAction[]> {
    const { data, error } = await (supabase as any)
      .from('compliance_actions')
      .select('*')
      .eq('status', status)
      .order('due_date', { ascending: true });

    if (error) throw error;
    return data || [];
  }

  async createAction(action: Partial<ComplianceAction>): Promise<ComplianceAction> {
    const { data, error } = await (supabase as any)
      .from('compliance_actions')
      .insert(action)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async updateAction(actionId: string, updates: Partial<ComplianceAction>): Promise<ComplianceAction> {
    const { data, error } = await (supabase as any)
      .from('compliance_actions')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', actionId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  // Initialization
  async initializeEntityCompliance(
    entityType: string,
    entityId: string,
    frameworkIds?: string[]
  ): Promise<number> {
    const { data, error } = await (supabase as any)
      .rpc('initialize_entity_compliance', {
        p_entity_type: entityType,
        p_entity_id: entityId,
        p_framework_ids: frameworkIds || null,
      });

    if (error) throw error;
    return data || 0;
  }

  // Alerts
  async generateComplianceAlerts(): Promise<Array<{
    entity_type: string;
    entity_id: string;
    alert_type: string;
    alert_message: string;
    severity: string;
  }>> {
    const { data, error } = await (supabase as any)
      .rpc('generate_compliance_alerts');

    if (error) throw error;
    return data || [];
  }

  // Context-Aware Compliance
  async getTenantApplicableFrameworks(tenantId: string): Promise<ApplicableFramework[]> {
    const { data, error } = await (supabase as any)
      .rpc('get_tenant_applicable_frameworks', {
        p_tenant_id: tenantId,
      });

    if (error) throw error;
    return data || [];
  }

  async autoDetectFrameworks(tenantId: string): Promise<number> {
    const { data, error } = await (supabase as any)
      .rpc('auto_detect_applicable_frameworks', {
        p_tenant_id: tenantId,
      });

    if (error) throw error;
    return data || 0;
  }

  async configureTenantFramework(
    tenantId: string,
    frameworkId: string,
    config: Partial<TenantComplianceConfig>
  ): Promise<TenantComplianceConfig> {
    const { data, error } = await (supabase as any)
      .from('tenant_compliance_configuration')
      .upsert({
        platform_tenant_id: tenantId,
        framework_id: frameworkId,
        ...config,
        updated_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async getTenantComplianceConfig(tenantId: string): Promise<TenantComplianceConfig[]> {
    const { data, error } = await (supabase as any)
      .from('tenant_compliance_configuration')
      .select('*')
      .eq('platform_tenant_id', tenantId)
      .order('priority');

    if (error) throw error;
    return data || [];
  }
}
